/**
 * FeedbackRecipientsPanel (09/2026)
 * ─────────────────────────────────
 * Konfiguriert, WER weitergeleitetes Kunden-Feedback bekommt:
 *  - owner      → nur der Owner (Alex)
 *  - all_admins → alle aktiven Admins & Trainer
 *  - custom     → ausgewählte Personen
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { key: 'owner', label: 'Nur Owner (Alex)', hint: 'Standard – alles geht an den Inhaber' },
  { key: 'all_admins', label: 'Alle Admins & Trainer', hint: 'Jede/r im Team wird informiert' },
  { key: 'custom', label: 'Bestimmte Personen', hint: 'Du wählst gezielt aus' },
];

const FeedbackRecipientsPanel = () => {
  const [mode, setMode] = useState('owner');
  const [userIds, setUserIds] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/chatbot/feedback-recipients`, { headers: authHeaders });
      setMode(data.mode || 'owner');
      setUserIds(data.user_ids || []);
      setCandidates(data.candidates || []);
      setResolved(data.resolved || []);
    } catch (e) {
      toast.error('Empfänger konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const toggleUser = (id) => {
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = async () => {
    if (mode === 'custom' && userIds.length === 0) {
      toast.error('Bitte mindestens eine Person auswählen');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/api/chatbot/feedback-recipients`,
        { mode, user_ids: userIds },
        { headers: authHeaders }
      );
      setResolved(data.resolved || []);
      toast.success('✅ Feedback-Empfänger gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const nameOf = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || c.id;

  return (
    <Card className="border-2 border-sky-500/30" data-testid="feedback-recipients-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-sky-500" />
          Feedback-Empfänger
        </CardTitle>
        <CardDescription>
          Wenn der KI-Bot Kunden-Feedback, Kritik oder Wünsche weiterleitet, geht die
          Benachrichtigung (WhatsApp + E-Mail + Glocke) an diese Personen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-gray-500">Lade…</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  data-testid={`feedback-mode-${m.key}`}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    mode === m.key
                      ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                      : 'border-gray-200 hover:border-sky-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3 h-3 rounded-full border ${
                        mode === m.key ? 'bg-sky-500 border-sky-500' : 'border-gray-400'
                      }`}
                    />
                    <span className="font-medium text-sm">{m.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-5">{m.hint}</p>
                </button>
              ))}
            </div>

            {mode === 'custom' && (
              <div className="border rounded-lg p-3 space-y-2 bg-white dark:bg-gray-900">
                <p className="text-xs text-gray-500">Personen auswählen (Admins & Trainer):</p>
                <div className="flex flex-wrap gap-2">
                  {candidates.length === 0 && (
                    <p className="text-sm text-gray-400">Keine Mitarbeiter gefunden.</p>
                  )}
                  {candidates.map((c) => {
                    const active = userIds.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        data-testid={`feedback-candidate-${c.id}`}
                        className={`flex items-center gap-2 px-3 py-1.5 border rounded-full cursor-pointer text-sm ${
                          active ? 'bg-sky-100 border-sky-400 dark:bg-sky-900/30' : 'hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleUser(c.id)}
                          className="accent-sky-500"
                        />
                        <span>{nameOf(c)}</span>
                        <Badge variant="outline" className="text-[10px]">{c.role}</Badge>
                        {!c.phone && !c.email && (
                          <span className="text-[10px] text-amber-600">kein Kontakt</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {resolved.length > 0 && (
              <div className="text-xs text-gray-600 bg-gray-50 dark:bg-gray-800 rounded p-2">
                <span className="font-medium">Aktuell aktiv: </span>
                {resolved.map((r) => r.name || r.email).filter(Boolean).join(', ')}
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="save-feedback-recipients-btn"
                className="bg-sky-600 hover:bg-sky-700">
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Speichern…' : 'Empfänger speichern'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackRecipientsPanel;
